<script lang="ts" setup>
import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { bgColors } from '@/services/application/constantes'
import Loading from '@/components/common/loading.vue'
import PhInfo from '~icons/ph/info'

const props = withDefaults(defineProps<{
  disabled?: boolean
  toggleIs?: 'right' | 'left'
  modelValue?: boolean
  label?: string
  ariaLabel?: string
  bgColor?: keyof typeof bgColors
  loading?: boolean
  tooltip?: string | { content: string, html?: boolean }
  /** small: a lighter switch for long lists of settings */
  size?: 'default' | 'small'
}>(), {
  bgColor: 'primary',
  toggleIs: 'right',
  disabled: false,
  loading: false,
  tooltip: undefined,
  size: 'default',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t } = useI18n()
const switchId = useId()

const toggleClass = {
  right: '',
  left: 'flex-row-reverse justify-end',
}

const accessibleName = computed(() => props.ariaLabel || undefined)
</script>

<template>
  <div class="flex items-center relative" :class="[toggleClass[toggleIs]]">
    <label
      :for="switchId"
      class="flex items-center cursor-pointer relative"
      :class="[toggleClass[toggleIs]]"
    >
      <input
        :id="switchId"
        :checked="modelValue"
        type="checkbox"
        role="switch"
        class="sr-only"
        :disabled="disabled || loading"
        :aria-checked="!!modelValue"
        :aria-label="accessibleName"
        :aria-busy="loading || undefined"
        @change="emit('update:modelValue', !modelValue)"
      >
      <span
        v-if="label"
        class="font-medium flex items-center gap-2"
        :class="toggleIs === 'right' ? 'mr-3' : 'ml-3'"
      >
        {{ label }}
      </span>
      <span class="relative" aria-hidden="true">
        <span
          class="block rounded-full shadow-inner transition-colors"
          :class="size === 'small' ? ['w-8 h-3', modelValue && !disabled ? 'bg-primary-500/30' : 'bg-gray-400/70'] : 'w-10 h-4 bg-gray-400'"
        />
        <span
          class="dot absolute rounded-full shadow-sm -left-1 -top-1 transition flex items-center justify-center"
          :class="[size === 'small' ? 'w-5 h-5' : 'w-6 h-6', disabled || loading ? 'bg-gray-600' : modelValue ? bgColors[bgColor] : 'bg-base']"
        >
          <Loading v-if="loading" />
        </span>
      </span>
    </label>
    <button
      v-if="tooltip"
      type="button"
      v-tooltip="tooltip"
      class="inline-flex cursor-help subtitle ml-2"
      :aria-label="t('common.a11y.moreInfo')"
      @click.prevent
    >
      <PhInfo class="text-base" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
input:checked ~ span .dot {
  transform: translateX(100%);
}
</style>
